import React, { useEffect, useRef, useState } from 'react';
import Add2WalletLogo from './Add2WalletLogo';
import { BRAND } from './brand';

// Typewriter next to the brand icon (mobile only): alternates the brand name
// with the tap-to-add nudge at a calm human pace, runs the loop at most
// MAX_CYCLES times, then settles permanently on the brand name (caret gone).
// This replaces the big mobile hero heading so the whole pass fits on one
// phone screen. Plain timeouts — no extra deps.
const TYPED_PHRASES = ['Add2Wallet.link', 'Tap below to Add2Wallet'];
const TYPE_MS = 160;
const HOLD_MS = 2600;
const MAX_CYCLES = 3;

const TypedBrand: React.FC = () => {
  const [text, setText] = useState('');
  const [settled, setSettled] = useState(false);

  // All progression state lives in this local machine and timers are
  // scheduled OUTSIDE setState. (Scheduling inside the updater doubled the
  // timer chain under React StrictMode — dev invokes updaters twice — which
  // made the typing accelerate and jitter.) No backspace animation: a
  // finished phrase holds, then clears in one go and the next one types.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    const s = { phrase: 0, cycles: 0, len: 0 };

    const step = () => {
      if (cancelled) return;
      const target = TYPED_PHRASES[s.phrase];

      s.len += 1;
      setText(target.slice(0, s.len));
      if (s.len < target.length) {
        timer = setTimeout(step, TYPE_MS);
        return;
      }

      // Phrase complete. Settle for good once the brand name finishes on the
      // final cycle; otherwise hold, clear instantly, and type the next one.
      if (s.phrase === 0 && s.cycles >= MAX_CYCLES) {
        setSettled(true);
        return;
      }
      timer = setTimeout(() => {
        if (cancelled) return;
        s.phrase = (s.phrase + 1) % TYPED_PHRASES.length;
        // Back to the brand name = one full loop completed.
        if (s.phrase === 0) s.cycles += 1;
        s.len = 0;
        setText('');
        timer = setTimeout(step, TYPE_MS * 2);
      }, HOLD_MS);
    };

    timer = setTimeout(step, TYPE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  return (
    <span className="text-base font-bold tracking-tight whitespace-nowrap" style={{ color: BRAND.ink }}>
      {text}
      {!settled && (
        <>
          <span className="typed-caret" aria-hidden>|</span>
          <style>{`
            .typed-caret { color: ${BRAND.limeDark}; animation: a2w-caret 1s step-end infinite; }
            @keyframes a2w-caret { 50% { opacity: 0; } }
          `}</style>
        </>
      )}
    </span>
  );
};

// Page chrome header: brand icon + (desktop) static wordmark / (mobile)
// typewriter nudge, language pill + menu on the right.
const SiteHeader: React.FC<{ onContactSupport?: () => void }> = ({ onContactSupport }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  return (
    <header className="w-full bg-white/80 backdrop-blur border-b border-[#E4EAE2]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 min-w-0">
          {/* Desktop: full lockup. Mobile: icon + typing nudge. */}
          <span className="hidden lg:inline-flex"><Add2WalletLogo /></span>
          <span className="inline-flex items-center gap-2 lg:hidden min-w-0">
            <Add2WalletLogo showWordmark={false} />
            <TypedBrand />
          </span>
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#E4EAE2] bg-white text-sm text-gray-700 hover:bg-gray-50 transition"
            aria-label="Language: English"
          >
            <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3z" />
            </svg>
            English
            <svg className="w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#E4EAE2] bg-white text-gray-700 hover:bg-gray-50 transition"
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-[#E4EAE2] py-1.5 z-40">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onContactSupport?.(); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Contact support
                </button>
                <a
                  href="https://add2wallet.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  About Add2Wallet
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default SiteHeader;
